import {
  TERMS_FEE,
  AI_FREE_TRIES,
  AI_PRICE_GEL,
  MEDIA_PRICE_PER_GB,
  MEDIA_SERVICE_DAYS,
  MEDIA_GRACE_DAYS,
  ADDON_REFUND_DAYS,
} from './termsContent'

const MEDIA_PRICE_LABEL = MEDIA_PRICE_PER_GB.toFixed(2)

/** ISO date (YYYY-MM-DD). Update whenever you change the refund policy below. */
export const REFUND_LAST_UPDATED = '2026-09-03'

export const refundContent = {
  en: {
    title: 'Storyfy.ge — Refund Policy',
    lastUpdatedLabel: 'Last updated:',
    intro: [
      'This Refund Policy governs refund procedures related to transactions conducted through the Storyfy.ge platform.',
      'Two different kinds of payment pass through the platform, and each follows its own rules: (A) monetary gifts sent by guests to an event organizer, for which Storyfy.ge acts only as an intermediary; and (B) Storyfy.ge’s own paid services — AI generation credits and event album storage — which the Company sells directly. Part A of this policy covers gifts; Part B covers paid services. Invitation templates and every design adjustment (colour, background, font) are free of charge: no payment is taken for them and there is nothing to refund.',
    ],
    sections: [
      {
        title: 'Part A — Gifts sent to organizers',
      },
      {
        title: '1. Nature of the Service',
        paragraph:
          'Storyfy.ge acts solely as a technological intermediary platform between event organizers and guests. Payments made through the platform constitute voluntary monetary gifts directed to the organizer/beneficiary.',
      },
      {
        title: '2. Eligibility for Refunds',
        intro:
          'A refund may only be requested and processed if ALL of the following conditions are met:',
        list: [
          'the event has been officially canceled by the organizer',
          'the refund request is submitted before the gifted funds have been transferred to the organizer’s personal bank account',
          'the transaction is technically reversible under the acquiring bank or payment provider rules',
        ],
      },
      {
        title: '3. Non-Refundable Situations',
        intro: 'Refunds will NOT be available in the following cases:',
        list: [
          'the funds have already been transferred to the organizer',
          'the guest changed their mind after completing the transaction',
          'disputes arise between the guest and organizer regarding the use of gifted funds',
          'the organizer fails to purchase wishlist items displayed on the platform',
          'delays or issues are caused by third-party banking systems outside Storyfy.ge’s control',
        ],
      },
      {
        title: '4. How to Request a Refund',
        paragraphs: [
          'Refund requests are submitted by email to the address at the end of this document. Please include the payment reference shown on the confirmation screen or in the bank statement, the event name, the amount, and the reason for the request.',
          'Requests should be sent as soon as possible, and in any case before the funds are paid out to the organizer. Once a payout has been made, the platform can no longer reverse the transaction.',
        ],
      },
      {
        title: `5. The ${TERMS_FEE} Commission`,
        paragraph: `Where a gift is refunded to the guest, the ${TERMS_FEE} commission withheld on that gift is refunded with it. The commission is never retained on a reversed gift. Any referral bonus credited on that gift is reversed at the same time.`,
      },
      {
        title: 'Part B — Storyfy.ge’s own paid services',
      },
      {
        title: '6. AI Generation Credits',
        list: [
          {
            label: '6.1.',
            text: `Each account receives ${AI_FREE_TRIES} free invitation generations. Free generations have no monetary value and are not refundable in cash.`,
          },
          {
            label: '6.2.',
            text: `Additional generations are purchased as credits at ${AI_PRICE_GEL} GEL each. Unused credits may be refunded within ${ADDON_REFUND_DAYS} days of purchase, on request.`,
          },
          {
            label: '6.3.',
            text: 'A generation that has already been delivered is not refundable, because the service has been performed. This applies whether the design was produced by the external AI provider or by the platform’s built-in generator.',
            emphasize: ['not refundable'],
          },
          {
            label: '6.4.',
            text: 'A generation that fails for technical reasons consumes neither a free try nor a paid credit, so there is nothing to refund.',
          },
        ],
      },
      {
        title: '7. Event Album Storage',
        list: [
          {
            label: '7.1.',
            text: `Album storage is sold per gigabyte at a flat rate of ${MEDIA_PRICE_LABEL} GEL per GB. Payment starts the service immediately: the ${MEDIA_SERVICE_DAYS}-day storage window opens as soon as the first purchase for that event is confirmed.`,
          },
          {
            label: '7.2.',
            text: `A purchase may be refunded in full within ${ADDON_REFUND_DAYS} days if no file has yet been uploaded to the album. Once files have been uploaded, the service is in use and the purchase is not refundable.`,
          },
          {
            label: '7.3.',
            text: 'Top-ups follow the same rule: refundable within the same period only to the extent that the added capacity has not been used.',
          },
          {
            label: '7.4.',
            text: `No refund is due for the unused remainder of a storage window, for capacity that was bought but not filled, or for files lost after the ${MEDIA_SERVICE_DAYS}-day window and the ${MEDIA_GRACE_DAYS}-day grace period have passed, or after the organizer deletes the event.`,
          },
        ],
      },
      {
        title: '8. Statutory Withdrawal Right',
        paragraph:
          'By purchasing a paid service and starting to use it immediately, the buyer requests that performance begin at once and acknowledges that the statutory right of withdrawal from a distance contract is lost once the service has been fully performed. Nothing in this policy limits rights that cannot be waived under the consumer protection legislation of Georgia.',
      },
      {
        title: 'General provisions',
      },
      {
        title: '9. Refund Processing Time',
        paragraphs: [
          'Approved refunds are generally processed within 5–10 business days depending on the acquiring bank, payment provider, and the customer’s issuing bank.',
          'Storyfy.ge is not responsible for delays caused by financial institutions.',
        ],
      },
      {
        title: '10. Transaction and Banking Fees',
        paragraph:
          'Any non-refundable banking, acquiring, or transaction fees charged during the original payment process may be deducted from the refunded amount where permitted by applicable law and payment provider rules.',
      },
      {
        title: '11. Chargebacks and Fraud Prevention',
        paragraph:
          'Storyfy.ge reserves the right to suspend accounts, delay payouts, or provide transaction information to financial institutions and law enforcement authorities in cases involving suspected fraud, unauthorized transactions, abuse of the platform, or violations of applicable law. Where a chargeback is raised on a gift that has already been paid out, or on a paid service that has been used, the Company may recover the amount from the account holder’s balance.',
      },
      {
        title: '12. Contact',
        intro: 'For refund-related inquiries, please contact:',
        contact: {
          emailLabel: 'Email:',
          websiteLabel: 'Website:',
        },
      },
    ],
  },
  ka: {
    title: 'Storyfy.ge — თანხის დაბრუნების პოლიტიკა',
    lastUpdatedLabel: 'ბოლო განახლების თარიღი:',
    intro: [
      'წინამდებარე დოკუმენტი არეგულირებს Storyfy.ge პლატფორმის საშუალებით განხორციელებულ ტრანზაქციებთან დაკავშირებულ თანხის დაბრუნების წესებს.',
      'პლატფორმაზე ორი განსხვავებული ტიპის გადახდა ხორციელდება და თითოეულს თავისი წესები აქვს: (ა) სტუმრების მიერ ღონისძიების ორგანიზატორისთვის გაგზავნილი ფულადი საჩუქრები, სადაც Storyfy.ge მხოლოდ შუამავალია; და (ბ) Storyfy.ge-ის საკუთარი ფასიანი სერვისები — ხელოვნური ინტელექტის კრედიტები და ღონისძიების ალბომის სივრცე — რომლებსაც კომპანია უშუალოდ ჰყიდის. წინამდებარე პოლიტიკის „ა“ ნაწილი ეხება საჩუქრებს, „ბ“ ნაწილი — ფასიან სერვისებს. მოწვევის შაბლონები და დიზაინის ყველა ცვლილება (ფერი, ფონი, შრიფტი) უფასოა: მათზე გადასახადი არ იღება და დასაბრუნებელი არაფერია.',
    ],
    sections: [
      {
        title: 'ნაწილი ა — ორგანიზატორისთვის გაგზავნილი საჩუქრები',
      },
      {
        title: '1. მომსახურების ხასიათი',
        paragraph:
          'Storyfy.ge წარმოადგენს ტექნოლოგიურ შუამავალ პლატფორმას ღონისძიების ორგანიზატორსა და სტუმარს შორის. პლატფორმის საშუალებით განხორციელებული გადახდები ითვლება ორგანიზატორის/ბენეფიციარის მიმართ ნებაყოფლობით ფულად საჩუქრად.',
      },
      {
        title: '2. თანხის დაბრუნების პირობები',
        intro:
          'თანხის დაბრუნება შესაძლებელია მხოლოდ იმ შემთხვევაში, თუ სრულდება ყველა ქვემოთ ჩამოთვლილი პირობა:',
        list: [
          'ღონისძიება ოფიციალურად გაუქმებულია ორგანიზატორის მიერ',
          'მოთხოვნა წარმოდგენილია მანამ, სანამ თანხა გადაირიცხება ორგანიზატორის პირად საბანკო ანგარიშზე',
          'ტრანზაქციის დაბრუნება ტექნიკურად შესაძლებელია ბანკისა და საგადახდო პროვაიდერის წესების შესაბამისად',
        ],
      },
      {
        title: '3. არადაბრუნებადი შემთხვევები',
        intro: 'თანხის დაბრუნება არ განხორციელდება შემდეგ შემთხვევებში:',
        list: [
          'თანხა უკვე გადარიცხულია ორგანიზატორის საბანკო ანგარიშზე',
          'სტუმარმა გადაიფიქრა ტრანზაქციის განხორციელების შემდეგ',
          'სტუმარსა და ორგანიზატორს შორის არსებობს დავა თანხის შემდგომ გამოყენებასთან დაკავშირებით',
          'ორგანიზატორმა არ შეიძინა ვიშლისტში მითითებული ნივთები',
          'შეფერხება გამოწვეულია მესამე მხარის საბანკო ან საგადახდო სისტემების მიერ',
        ],
      },
      {
        title: '4. როგორ მოვითხოვოთ თანხის დაბრუნება',
        paragraphs: [
          'თანხის დაბრუნების მოთხოვნა იგზავნება დოკუმენტის ბოლოს მითითებულ ელ-ფოსტაზე. გთხოვთ, მიუთითოთ გადახდის საიდენტიფიკაციო კოდი (ასახულია დადასტურების ეკრანზე ან საბანკო ამონაწერში), ღონისძიების დასახელება, თანხა და მოთხოვნის მიზეზი.',
          'მოთხოვნა უნდა გამოიგზავნოს რაც შეიძლება მალე და ნებისმიერ შემთხვევაში მანამ, სანამ თანხა გაიცემა ორგანიზატორზე. გაცემის შემდეგ პლატფორმა ტრანზაქციის დაბრუნებას ვეღარ ახორციელებს.',
        ],
      },
      {
        title: `5. ${TERMS_FEE}-იანი საკომისიო`,
        paragraph: `თუ საჩუქარი უბრუნდება სტუმარს, მასზე დაკავებული ${TERMS_FEE}-იანი საკომისიოც უბრუნდება. დაბრუნებულ საჩუქარზე საკომისიო არასდროს რჩება კომპანიას. ამავე დროს უქმდება ამ საჩუქარზე დარიცხული სარეფერალო ბონუსიც.`,
      },
      {
        title: 'ნაწილი ბ — Storyfy.ge-ის საკუთარი ფასიანი სერვისები',
      },
      {
        title: '6. ხელოვნური ინტელექტის კრედიტები',
        list: [
          {
            label: '6.1.',
            text: `თითოეული ანგარიში იღებს ${AI_FREE_TRIES} უფასო გენერაციას. უფასო გენერაციებს ფულადი ღირებულება არ აქვს და ფულით არ ანაზღაურდება.`,
          },
          {
            label: '6.2.',
            text: `დამატებითი გენერაციები იძენება კრედიტების სახით, თითო ${AI_PRICE_GEL} ლარად. გამოუყენებელი კრედიტები მოთხოვნის საფუძველზე ბრუნდება შეძენიდან ${ADDON_REFUND_DAYS} დღის განმავლობაში.`,
          },
          {
            label: '6.3.',
            text: 'უკვე მიწოდებული გენერაცია დაბრუნებას არ ექვემდებარება, ვინაიდან მომსახურება შესრულებულია. ეს წესი მოქმედებს იმის მიუხედავად, დიზაინი გარე პროვაიდერმა შექმნა თუ პლატფორმის ჩაშენებულმა გენერატორმა.',
            emphasize: ['დაბრუნებას არ ექვემდებარება'],
          },
          {
            label: '6.4.',
            text: 'ტექნიკური მიზეზით ჩავარდნილი გენერაცია არ ხარჯავს არც უფასო ცდას და არც ანაზღაურებულ კრედიტს, შესაბამისად დასაბრუნებელი თანხაც არ წარმოიშობა.',
          },
        ],
      },
      {
        title: '7. ღონისძიების ალბომის სივრცე',
        list: [
          {
            label: '7.1.',
            text: `ალბომის სივრცე იყიდება გიგაბაიტობით, ერთიანი ტარიფით — ${MEDIA_PRICE_LABEL} ლარი 1 GB-ზე. გადახდა მომსახურებას იწყებს დაუყოვნებლივ: ${MEDIA_SERVICE_DAYS}-დღიანი სამომსახურო ვადა იხსნება ღონისძიებაზე პირველი შეძენის დადასტურებისთანავე.`,
          },
          {
            label: '7.2.',
            text: `შეძენილი თანხა სრულად ბრუნდება ${ADDON_REFUND_DAYS} დღის განმავლობაში, თუ ალბომში ჯერ არცერთი ფაილი არ არის ატვირთული. ფაილების ატვირთვის შემდეგ მომსახურება გამოყენებულად ითვლება და თანხა დაბრუნებას აღარ ექვემდებარება.`,
          },
          {
            label: '7.3.',
            text: 'დამატებით შევსებაზე იგივე წესი ვრცელდება: თანხა ბრუნდება იმავე ვადაში და მხოლოდ იმ ნაწილში, რომელშიც დამატებული მოცულობა გამოყენებული არ არის.',
          },
          {
            label: '7.4.',
            text: `თანხა არ ბრუნდება სამომსახურო ვადის გამოუყენებელ ნაწილზე, შეძენილ, მაგრამ შეუვსებელ მოცულობაზე, ასევე იმ ფაილებზე, რომლებიც დაიკარგა ${MEDIA_SERVICE_DAYS}-დღიანი ვადისა და ${MEDIA_GRACE_DAYS}-დღიანი საშეღავათო ვადის გასვლის შემდეგ ან ორგანიზატორის მიერ ღონისძიების წაშლის შედეგად.`,
          },
        ],
      },
      {
        title: '8. კანონით გათვალისწინებული უარის თქმის უფლება',
        paragraph:
          'ფასიანი სერვისის შეძენით და მისი დაუყოვნებლივ გამოყენების დაწყებით მყიდველი ითხოვს მომსახურების დაუყოვნებლივ დაწყებას და ადასტურებს, რომ დისტანციური ხელშეკრულებიდან უარის თქმის კანონისმიერი უფლება უქმდება მომსახურების სრულად შესრულების შემდეგ. წინამდებარე პოლიტიკა არ ზღუდავს იმ უფლებებს, რომელთა უარყოფაც საქართველოს მომხმარებელთა უფლებების დაცვის კანონმდებლობით დაუშვებელია.',
      },
      {
        title: 'ზოგადი დებულებები',
      },
      {
        title: '9. თანხის დაბრუნების ვადა',
        paragraphs: [
          'დამტკიცებული თანხის დაბრუნება, როგორც წესი, მუშავდება 5-10 სამუშაო დღის განმავლობაში, ბანკისა და საგადახდო სისტემის წესების შესაბამისად.',
          'Storyfy.ge პასუხისმგებელი არ არის ფინანსური ინსტიტუტების მიერ გამოწვეულ დაგვიანებაზე.',
        ],
      },
      {
        title: '10. საბანკო და ტრანზაქციული საკომისიოები',
        paragraph:
          'გადახდის პროცესში დაკისრებული არასაბრუნებელი საბანკო, ეკვაირინგის ან ტრანზაქციული საკომისიოები შესაძლოა დაკავდეს დასაბრუნებელი თანხიდან, მოქმედი კანონმდებლობისა და საგადახდო სისტემის წესების შესაბამისად.',
      },
      {
        title: '11. Chargeback და თაღლითობის პრევენცია',
        paragraph:
          'Storyfy.ge უფლებამოსილია შეაჩეროს თანხის გაცემა, დროებით დაბლოკოს ანგარიში ან შესაბამის ორგანოებს გადასცეს ინფორმაცია თაღლითობის, არასანქცირებული ტრანზაქციის, პლატფორმის ბოროტად გამოყენების ან კანონდარღვევის ეჭვის არსებობის შემთხვევაში. თუ ჩარჯბექი დაფიქსირდა უკვე გაცემულ საჩუქარზე ან უკვე გამოყენებულ ფასიან სერვისზე, კომპანია უფლებამოსილია შესაბამისი თანხა დაიბრუნოს ანგარიშის მფლობელის ბალანსიდან.',
      },
      {
        title: '12. საკონტაქტო ინფორმაცია',
        intro: 'დამატებითი ინფორმაციისთვის დაგვიკავშირდით:',
        contact: {
          emailLabel: 'ელ-ფოსტა:',
          websiteLabel: 'ვებ-გვერდი:',
        },
      },
    ],
  },
}
